import { Helmet } from 'react-helmet';
import { useEffect, useState, useMemo } from 'react';
import * as S from './Home.styles';
import { CDN_BASE_URL } from '../../config';
import Marquee from 'react-fast-marquee';
import Dialog from '@mui/material/Dialog';
import { useNavigate } from 'react-router-dom';
import { getSiteContent } from '../../api/siteContentApi';

const DEFAULT_EN = {
  title: 'GATE Selective Entry Exam',
  subtitle: 'Outstanding Results from Uplus Education Students',
  items: [
    { text: '4 students scored above 300 in the 2025 GATE exam (302 / 307 / 308 / 309 — all from the same 5-student advanced class).', highlights: ['4 students'] },
    { text: '1 student achieved the State Highest score in 2025 GATE Quantitative Reasoning.', highlights: ['1 student'] },
    { text: '4 students have achieved the State Highest score in 2025 GATE Communicating Ideas.', highlights: ['4 students'] },
    { text: '30+ students receive Perth Modern Offers or private school scholarships every year.', highlights: ['30+ students'] },
  ],
  body: [
    { text: 'Behind every result is a long-term, sustainable teaching system. To date, Uplus Education has helped over 260 students gain entry into GATE programs or receive private school scholarships.', highlights: ['260 students'] },
    { text: 'We have always believed that results are an outcome, not the sole objective. Our achievements stem from systematic curriculum design, consistent teaching execution, strong parental support, and the daily effort and good study habits cultivated by our students.' },
    { text: 'Every outstanding result is the natural product of collective commitment and long-term dedication.' },
  ],
};

const DEFAULT_ZH = {
  title: 'GATE Selective Entry Exam',
  subtitle: 'Uplus Education 学员荣获佳绩',
  items: [
    { text: '4名学员在2025年GATE考试中总分突破300分（302 / 307 / 308 / 309 —— 来自同一个5人封闭高阶班级）。', highlights: ['4名'] },
    { text: '1名学员在2025年GATE考试中荣获Quantitative Reasoning State Highest 数学逻辑州最高分。', highlights: ['1名'] },
    { text: '4名学员在2025年GATE考试中荣获Communicating Ideas State Highest 写作州最高分。', highlights: ['4名'] },
    { text: '每年30+学生都获得了Perth Modern Offer / 私校奖学金。', highlights: ['每年30+'] },
  ],
  body: [
    { text: '成绩的背后，是长期、可持续的教学体系。在过往教学中，Uplus Education 累计已有260+名学员成功考入GATE班级或获得私校奖学金。', highlights: ['260+名'] },
    { text: '我们始终相信，成绩只是结果，而非唯一目标。Uplus的教学成果，来自系统化的课程设计、长期坚持的教学执行、家长的高度配合，以及学生日复一日的努力与良好学习习惯的养成。' },
    { text: '每一份亮眼成绩，都是多方共同投入、长期积累后的自然结果。' },
  ],
};

// Render text with highlighted segments (first match of each highlight string)
const renderWithHighlights = (text, highlights) => {
  if (!highlights || highlights.length === 0 || !text) return text;

  // Find first occurrence of each highlight
  const matches = [];
  for (const h of highlights) {
    if (!h) continue;
    const idx = text.indexOf(h);
    if (idx !== -1) {
      matches.push({ start: idx, end: idx + h.length, text: h });
    }
  }

  if (matches.length === 0) return text;

  // Sort by position, remove overlaps
  matches.sort((a, b) => a.start - b.start);
  const filtered = [matches[0]];
  for (let i = 1; i < matches.length; i++) {
    if (matches[i].start >= filtered[filtered.length - 1].end) {
      filtered.push(matches[i]);
    }
  }

  // Build segments
  const segments = [];
  let pos = 0;
  filtered.forEach((m, i) => {
    if (pos < m.start) segments.push(text.slice(pos, m.start));
    segments.push(<S.AchievementRed key={i}>{m.text}</S.AchievementRed>);
    pos = m.end;
  });
  if (pos < text.length) segments.push(text.slice(pos));

  return segments;
};

const getDefaultLang = () => {
  const lang = navigator.language || navigator.userLanguage || '';
  return lang.startsWith('zh') ? 'zh' : 'en';
};

const Home = () => {
  const [imageUrls, setImageUrls] = useState([]);
  const [hidden, setHidden] = useState({});
  const [openIdx, setOpenIdx] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [heroError, setHeroError] = useState(false);
  const [lang, setLang] = useState(getDefaultLang);
  const [customEn, setCustomEn] = useState(null);
  const [customZh, setCustomZh] = useState(null);
  const navigate = useNavigate();

  // Load custom achievement content from API
  useEffect(() => {
    getSiteContent('home_achievement_en').then(data => { if (data) setCustomEn(data); });
    getSiteContent('home_achievement_zh').then(data => { if (data) setCustomZh(data); });
  }, []);

  const achievementContent = useMemo(() => {
    if (lang === 'zh') {
      const src = customZh || DEFAULT_ZH;
      return { ...src, toggle: 'English' };
    }
    const src = customEn || DEFAULT_EN;
    return { ...src, toggle: '中文' };
  }, [lang, customEn, customZh]);

  useEffect(() => {
    const urls = [];
    for (let i = 1; i <= 17; i++) {
      const num = String(i).padStart(3, '0');
      urls.push(`${CDN_BASE_URL}/static/home/${num}.jpg`);
    }
    setImageUrls(urls);

    // 预加载Hero背景图
    const heroImg = new Image();
    heroImg.onload = () => setHeroLoaded(true);
    heroImg.onerror = () => {
      console.error('Hero image failed to load');
      setHeroError(true);
      setHeroLoaded(true); // 即使失败也设置为已加载，让页面继续显示
    };
    heroImg.src = `${CDN_BASE_URL}/static/Home.jpg`;

    // 设置备用超时机制，3秒后强制显示内容
    const fallbackTimer = setTimeout(() => {
      if (!heroLoaded) {
        console.warn('Hero image loading timeout, showing content anyway');
        setHeroLoaded(true);
      }
    }, 3000);

    return () => clearTimeout(fallbackTimer);
  }, [heroLoaded]);

  const handleImgError = (url) => {
    setHidden((prev) => ({ ...prev, [url]: true }));
  };

  const handleImgLoad = (url) => {
    setLoadedImages((prev) => ({ ...prev, [url]: true }));
  };

  return (
    <S.Container>
      <Helmet>
        <title>Uplus Education - Home</title>
        <meta name="description" content="Welcome to Uplus Education" />
      </Helmet>
      
      {/* Hero 区域 - 始终渲染，但控制透明度 */}
      <S.Hero 
        bg={heroError ? 'none' : CDN_BASE_URL + '/static/Home.jpg'}
        style={{
          opacity: heroLoaded ? 1 : 0,
          transition: 'opacity 0.8s ease-in-out',
          background: heroError ? 'linear-gradient(135deg, #F1AF59 0%, #e8963a 100%)' : undefined
        }}
      >
        <S.HeroContent>
          <S.HeroLogo src={CDN_BASE_URL + '/static/logo.png'} alt="Uplus Education" />
          <S.HeroMotto>UPLIFT &nbsp;|&nbsp; PERSEVERE &nbsp;|&nbsp; LEAD &nbsp;|&nbsp; UNDERSTAND &nbsp;|&nbsp; SUPPORT</S.HeroMotto>
          <S.HeroMottoZh>鼓舞 ｜ 坚持 ｜ 引导 ｜ 理解 ｜ 支持</S.HeroMottoZh>
          <S.HeroVisionLabel>Our Vision</S.HeroVisionLabel>
          <S.HeroVision>
            At Uplus Education, we envision a generation of confident, independent learners who think critically, act with purpose, and grow with resilience in an ever-changing world.
          </S.HeroVision>
          <S.HeroVisionZh>
            在优加教育，我们致力于培养一代自信、独立的学习者，使他们具备批判性思维、明确的目标感，并在不断变化的世界中持续成长与突破。
          </S.HeroVisionZh>
          <S.ButtonGroup>
            <S.Button variant="outline" onClick={() => navigate('/assessment')}>
              Book a Free Assessment
            </S.Button>
            <S.Button onClick={() => navigate('/courses')}>Learn More</S.Button>
          </S.ButtonGroup>
        </S.HeroContent>
      </S.Hero>
      
      {/* 只有当 Hero 加载完成后才显示其他内容 */}
      {heroLoaded && (
        <>
          {/* Student Achievements */}
          <S.AchievementCard>
            <S.AchievementToggle onClick={() => setLang(l => l === 'zh' ? 'en' : 'zh')}>
              {achievementContent.toggle}
            </S.AchievementToggle>
            <S.AchievementTitle>{achievementContent.title}</S.AchievementTitle>
            <S.AchievementSubtitle>{achievementContent.subtitle}</S.AchievementSubtitle>
            <S.AchievementDivider />
            <S.AchievementList>
              {achievementContent.items.map((item, i) => (
                <S.AchievementItem key={i}>
                  <S.AchievementBullet />
                  <S.AchievementText>
                    {renderWithHighlights(item.text, item.highlights)}
                  </S.AchievementText>
                </S.AchievementItem>
              ))}
            </S.AchievementList>
            <S.AchievementDivider />
            <S.AchievementBody>
              {achievementContent.body.map((para, i) => (
                <S.AchievementParagraph key={i}>
                  {renderWithHighlights(para.text, para.highlights)}
                </S.AchievementParagraph>
              ))}
            </S.AchievementBody>
          </S.AchievementCard>

          <S.LargeBannerImage
            src={CDN_BASE_URL + '/static/Home_02.png'}
            alt="Home 02"
            loading="lazy"
            style={{
              marginTop: '2rem',
              opacity: loadedImages[CDN_BASE_URL + '/static/Home_02.png'] ? 1 : 0,
              transition: 'opacity 0.5s ease-in-out'
            }}
            onLoad={() => handleImgLoad(CDN_BASE_URL + '/static/Home_02.png')}
            onError={() => handleImgError(CDN_BASE_URL + '/static/Home_02.png')}
          />

          <S.VideoContainer>
            <S.Video
              controls
              autoPlay
              muted
              loop
              preload="metadata"
              poster={CDN_BASE_URL + '/static/video-poster.jpg'} // 可选：视频封面图
            >
              <source src={CDN_BASE_URL + '/static/uplus.mp4'} type="video/mp4" />
              Your browser does not support the video tag.
            </S.Video>
          </S.VideoContainer>

          {/* 横向自动滚动图片墙 */}
          <S.Section style={{ padding: '2rem', marginBottom: 0, overflow: 'hidden' }}>
            <Marquee pauseOnHover gradient={false} speed={40} style={{ overflow: 'visible' }}>
              {imageUrls.map((url, idx) =>
                hidden[url] ? null : (
                  <img
                    key={url}
                    src={url}
                    alt={`Campus ${idx + 1}`}
                    loading="lazy"
                    style={{
                      borderRadius: 8,
                      width: 240, // 增大滚动图片宽度
                      height: 160, // 增大滚动图片高度
                      objectFit: 'cover',
                      marginRight: 20, // 增大间距
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      cursor: 'pointer',
                      opacity: loadedImages[url] ? 1 : 0,
                      transition: 'opacity 0.5s ease-in-out, transform 0.2s',
                    }}
                    onError={() => handleImgError(url)}
                    onLoad={() => handleImgLoad(url)}
                    onClick={() => setOpenIdx(idx)}
                    draggable={false}
                  />
                )
              )}
            </Marquee>
          </S.Section>

          {/* 图片放大预览 Dialog */}
          <Dialog open={openIdx !== null} onClose={() => setOpenIdx(null)} maxWidth="md">
            {openIdx !== null && (
              <img
                src={imageUrls[openIdx]}
                alt={`Preview ${openIdx + 1}`}
                style={{ maxWidth: '90vw', maxHeight: '80vh', display: 'block', margin: 'auto' }}
                onClick={() => setOpenIdx(null)}
              />
            )}
          </Dialog>
        </>
      )}
    </S.Container>
  );
};

export default Home;